/**
 * @param {number[]} - lines are represented by two consecutive implicit points in an array ([start x,])
 */
const getGridLine = (linePosition, strong) =>
  new fabric.Line(linePosition, {
    stroke: strong ? '#ccc' : '#eee',
    selectable: false,
    // potential performance improvements
    hasControls: false,
    hasBorders: false,
    hasRotatingPoint: false,
    // main perf improvement
    objectCaching: false,
    // don't include in JSON output
    excludeFromExport: true,
  });

/**
 * @todo Add managing grid size based on an item being dragged?
 *   Likely via methods on this class that the blueprint grid uses
 *   to detect and expand/contract the grid.
 */
export class BlueprintGrid {
  /** @property {Promise} loaded - The a promise that resolves once the grid background image has been set */
  loaded = null;

  /** @property {fabric.Object} ref - The fabric rect that functions as the background grid */
  ref = null;

  /** @property {import('../blueprint-graph.js').BlueprintGraph} graphEditor - The instance of the BlueprintGraph service this object is attached to */
  graphEditor = null;

  /**
   * @param {import('../blueprint-graph.js').BlueprintGraph} blueprintGraph - the blueprint graph service
   * @param {{ spacing: number, width: number, height: number }} config - options for layout and grid generation
   * @returns {BlueprintShape}
   */
  constructor(blueprintGraph, config = {}) {
    const { canvas } = blueprintGraph;
    const { spacing, width, height } = config;
    this.spacing = spacing;
    this.width = width;
    this.height = height;

    this.graphEditor = blueprintGraph;

    // Create the background rectangle
    const rect = new fabric.Rect({
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      width: this.width,
      height: this.height,
      fill: '#fff',
      stroke: '#ddd',
      paintFirst: 'stroke',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
      strokeWidth: 3,
      strokeUniform: false,
      objectCaching: false, // caching is slow
      excludeFromExport: true, // don't include in JSON output
    });

    // Group grid elements so identity check is straightforward (see `is` method below)
    const group = new fabric.Group([rect], {
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      // cache stuff
      excludeFromExport: true, // don't include in JSON output
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
    });

    group.add(...this.#generateGridLines(canvas));

    this.ref = group;

    canvas.add(this.ref);

    // always send to back so graph doesn't need to worry about it
    canvas.sendToBack(rect);
    canvas.sendToBack(this.ref);

    this.graphEditor.recenter();
    this.graphEditor.render();
  }

  #generateGridLines = canvas => {
    const lines = [];
    const tempStrongLines = [];

    // Add vertical lines
    for (let i = this.spacing; i < this.width; i += this.spacing) {
      const strong = !(i % (this.spacing * 5));
      const line = getGridLine(
        [
          i - this.width / 2, // start x
          -(this.height / 2), // start y
          i - this.width / 2, // end x
          this.height - this.height / 2, // end y
        ],
        strong,
      );
      canvas.add(line);
      (strong ? tempStrongLines : lines).push(line);
    }

    // Add horizontal lines
    for (let i = this.spacing; i < this.height; i += this.spacing) {
      const strong = !(i % (this.spacing * 5));
      const line = getGridLine(
        [
          0 - this.width / 2, // start x
          i - this.height / 2, // start y
          this.width - this.width / 2, // end x
          i - this.height / 2, // end y
        ],
        strong,
      );
      canvas.add(line);
      (strong ? tempStrongLines : lines).push(line);
    }

    // ensure strong lines are behind everything else
    tempStrongLines.forEach(canvas.sendToBack.bind(canvas));
    // but lighter lines are behind the strong ones
    lines.forEach(canvas.sendToBack.bind(canvas));

    return [...lines, ...tempStrongLines];
  };

  /**
   * @param {fabric.Object} target - The fabric object to compare with
   * the ref identity
   * @return {Boolean}
   */
  is = target => target === this.ref || this.ref._objects.includes(target);
}
